import { supabase } from './supabase';
import type { PersonnelSummaryRow } from './dsrEntry';

export interface PreviousDSRWorkforce {
  dsr: { id: string; report_date: string; status: string };
  entries: PersonnelSummaryRow[];
}

// Returns the most recent prior DSR for this project that has at least one
// non-deleted personnel summary row. Prefers status='submitted' but falls
// back to any status (draft/reviewed/flagged/amended). Returns null if no
// candidate exists — the caller hides the "Copy from Previous Day" button.
export async function getPreviousDSRWorkforce(
  projectId: string,
  beforeDate: string,
): Promise<PreviousDSRWorkforce | null> {
  const SELECT =
    'id, report_date, status, ' +
    'dsr_personnel_summaries!inner(' +
      'id, dsr_report_id, company_name, crew_type, personnel_count, ' +
      'sse_count, supervisor_name, notes' +
    ')';

  const { data, error } = await supabase
    .from('dsr_reports')
    .select(SELECT)
    .eq('project_id', projectId)
    .eq('is_deleted', false)
    .eq('dsr_personnel_summaries.is_deleted', false)
    .lt('report_date', beforeDate)
    .order('report_date', { ascending: false })
    .limit(5);
  if (error) return null;

  type Row = {
    id: string;
    report_date: string;
    status: string;
    dsr_personnel_summaries: PersonnelSummaryRow[];
  };
  const rows = (data ?? []) as unknown as Row[];
  if (rows.length === 0) return null;

  const submitted = rows.find((r) => r.status === 'submitted');
  const chosen = submitted ?? rows[0];
  if (!chosen || chosen.dsr_personnel_summaries.length === 0) return null;

  return {
    dsr: {
      id: chosen.id,
      report_date: chosen.report_date,
      status: chosen.status,
    },
    entries: chosen.dsr_personnel_summaries,
  };
}

// Writes N new rows into dsr_personnel_summaries for the target DSR,
// copying each source row's crew fields. Notes are intentionally cleared
// — they refer to the previous shift (weather, specific SSEs, etc.) and
// tend to mislead when carried over. Returns the number of rows written.
export async function copyWorkforceFromDSR(params: {
  targetDsrId: string;
  tenantId: string;
  sourceEntries: PersonnelSummaryRow[];
}): Promise<number> {
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) throw new Error('Not authenticated');

  if (params.sourceEntries.length === 0) return 0;

  const rows = params.sourceEntries.map((e) => ({
    dsr_report_id: params.targetDsrId,
    tenant_id: params.tenantId,
    created_by: userId,
    company_name: e.company_name,
    crew_type: e.crew_type,
    personnel_count: e.personnel_count,
    sse_count: e.sse_count,
    supervisor_name: e.supervisor_name,
    notes: null,
  }));

  const { error, count } = await supabase
    .from('dsr_personnel_summaries')
    .insert(rows, { count: 'exact' });
  if (error) throw new Error(error.message);
  return count ?? rows.length;
}
